import React, { useState } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';

const WEB3FORMS_KEY = (
  process.env.REACT_APP_WEB3FORMS_KEY || 'YOUR_WEB3FORMS_ACCESS_KEY'
).trim();
const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const hasValidAccessKey = UUID_V4_REGEX.test(WEB3FORMS_KEY);

const Contact = () => {
  const headRef = useScrollReveal();
  const formRef = useScrollReveal();

  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasValidAccessKey) {
      setStatus('error');
      setErrorMsg(
        'Contact form is not configured yet. Please set a valid Web3Forms access key.'
      );
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    const formData = new FormData(e.target);
    formData.append('access_key', WEB3FORMS_KEY);
    formData.append('subject', 'New message from portfolio');
    formData.append('from_name', 'Portfolio Contact Form');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
        setErrorMsg(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  const isSubmitting = status === 'submitting';

  return (
    <section
      id="contact"
      className="relative bg-bg text-ink py-28 overflow-hidden"
    >
      <div className="absolute inset-0 grid-backdrop opacity-30 pointer-events-none" />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(61,139,255,0.08), transparent 60%)',
        }}
      />

      <div className="relative container mx-auto px-6">
        <div ref={headRef} className="reveal max-w-3xl">
          <span className="eyebrow">05 / Contact</span>
          <h2 className="section-title mt-6 text-4xl sm:text-6xl md:text-7xl">
            Contact<span className="text-accent">.</span>
          </h2>
          <p className="mt-6 text-ink-muted text-lg">
            Use this as a way to reach out to me.
          </p>
        </div>

        <div
          ref={formRef}
          className="reveal-zoom mt-16 max-w-3xl mx-auto relative border border-line bg-surface"
        >
          <form onSubmit={handleSubmit} className="p-6 md:p-8 grid gap-5">
            {/* Honeypot (spam trap) */}
            <input
              type="checkbox"
              name="botcheck"
              tabIndex="-1"
              autoComplete="off"
              className="hidden"
              style={{ display: 'none' }}
            />

            <div className="grid gap-2">
              <label
                htmlFor="name"
                className="font-mono text-xs uppercase tracking-widest text-ink-muted"
              >
                Name
              </label>
              <input
                id="name"
                type="text"
                name="name"
                required
                placeholder="Your name"
                disabled={isSubmitting}
                className="w-full bg-bg border border-line text-ink p-3 focus:outline-none focus:border-accent transition-colors placeholder:text-ink-dim font-mono text-sm disabled:opacity-60"
              />
            </div>
            <div className="grid gap-2">
              <label
                htmlFor="email"
                className="font-mono text-xs uppercase tracking-widest text-ink-muted"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                name="email"
                required
                placeholder="you@domain.com"
                disabled={isSubmitting}
                className="w-full bg-bg border border-line text-ink p-3 focus:outline-none focus:border-accent transition-colors placeholder:text-ink-dim font-mono text-sm disabled:opacity-60"
              />
            </div>
            <div className="grid gap-2">
              <label
                htmlFor="message"
                className="font-mono text-xs uppercase tracking-widest text-ink-muted"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                placeholder="What&rsquo;s on your mind?"
                disabled={isSubmitting}
                className="w-full min-h-[160px] bg-bg border border-line text-ink p-3 focus:outline-none focus:border-accent transition-colors placeholder:text-ink-dim font-mono text-sm resize-y disabled:opacity-60"
              />
            </div>

            {status === 'success' && (
              <div className="border border-signal/60 bg-signal/5 px-4 py-3 font-mono text-sm text-signal">
                Message sent. I&rsquo;ll get back to you soon.
              </div>
            )}
            {status === 'error' && (
              <div className="border border-red-500/60 bg-red-500/5 px-4 py-3 font-mono text-sm text-red-400">
                {errorMsg}
              </div>
            )}

            <div className="flex items-center justify-end pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !hasValidAccessKey}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
                <span>→</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
